/**
 * "New ticket" as a CRM dialog.
 *
 * Only the subject is required. Everything else on this form is something the
 * operator often does not know yet when a ticket is opened - which client it
 * belongs to, who should take it - and a form that insists on knowing is a form
 * people work around by typing "?" into it.
 *
 * Every control here is now one of the app's own. The first version reached for
 * whatever HTML was nearest: two native `<select>`s that dropped an operating
 * system list on top of the dialog, and a bare text input for the department
 * that made everybody retype the same three words until the queue held four
 * spellings of one of them. They are a `MenuField` and a `ComboField` now - the
 * same controls the ticket detail and the rest of the CRM use.
 *
 * The party field is a `PartyPicker` rather than a `ClientPicker`: most support
 * requests that arrive by phone are from somebody who has never been invoiced,
 * and making an operator promote them to a full client before they can file the
 * ticket is how tickets end up filed against nobody.
 */
import { useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import {
  createTicket,
  getDepartments,
  type StaffMember,
  type TicketPriority,
} from "../../../lib/support";
import { staffOptionLabel } from "../../../lib/staff";
import { Btn, ComboField, MenuField, Modal } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { PartyPicker, type PartyRef } from "../_shared/PartyPicker";
import { PRIORITIES } from "./tone";

const UNASSIGNED = "none";

export function NewTicketModal({
  staff,
  onClose,
  onCreated,
}: {
  /** Already in directory order - active people first. See `_shared/staff.ts`. */
  staff: StaffMember[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const t = useT();
  const [subject, setSubject] = useState("");
  const [partyName, setPartyName] = useState("");
  const [party, setParty] = useState<PartyRef | null>(null);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [priority, setPriority] = useState<TicketPriority>("normal");
  const [department, setDepartment] = useState("");
  const [departments, setDepartments] = useState<string[]>([]);
  const [assignedTo, setAssignedTo] = useState(UNASSIGNED);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getDepartments()
      .then((r) => setDepartments(r.rows))
      .catch(() => undefined);
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!subject.trim()) return;
    setBusy(true);
    try {
      await createTicket({
        subject: subject.trim(),
        // One of the two, never both from this form: an operator picks one row
        // out of the panel, and which table it came from is what decides the
        // column. A converted lead can end up holding both, but only the server
        // side does that.
        clientId: party?.kind === "client" ? party.id : undefined,
        leadId: party?.kind === "lead" ? party.id : undefined,
        contactName: contactName.trim() || undefined,
        contactEmail: contactEmail.trim() || undefined,
        priority,
        department: department.trim() || undefined,
        assignedTo: assignedTo === UNASSIGNED ? undefined : assignedTo,
        message: message.trim() || undefined,
      });
      onCreated();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={t("support.addTitle")} label={t("support.addTitle")} onClose={onClose}>
      {/* `Modal` mounts no `PopHost` of its own, and a `MenuField` without one
          is a control that silently does not open. */}
      <PopHost>
        <form onSubmit={submit} className="mt-1 flex flex-col gap-3">
          <div>
            <span className="crm-lab !mt-0">{t("support.subject")} *</span>
            <label className="crm-inp">
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t("support.subjectPlaceholder")}
                autoComplete="off"
                autoFocus
                required
              />
            </label>
          </div>

          <div>
            <span className="crm-lab !mt-0">{t("support.party")}</span>
            <PartyPicker
              value={partyName}
              selected={party}
              onChange={(v) => {
                setPartyName(v);
                setParty(null);
              }}
              onPick={(picked) => {
                setParty(picked);
                setPartyName(picked.name);
              }}
              placeholder={t("support.partyPlaceholder")}
            />
            <p className="crm-sub mt-1.5 text-[11px]">{t("support.partyHint")}</p>
          </div>

          {/* `min-w-0` on each cell: a grid track sizes to its content, and an
              input's own minimum width is what pushed these apart before. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="min-w-0">
              <span className="crm-lab !mt-0">{t("support.contactName")}</span>
              <label className="crm-inp">
                <input
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder={t("support.contactNamePlaceholder")}
                  autoComplete="off"
                />
              </label>
            </div>
            <div className="min-w-0">
              <span className="crm-lab !mt-0">{t("support.contactEmail")}</span>
              <label className="crm-inp">
                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder={t("support.contactEmailPlaceholder")}
                  autoComplete="off"
                />
              </label>
            </div>
            <div className="min-w-0">
              <span className="crm-lab !mt-0">{t("support.priority")}</span>
              <MenuField
                variant="form"
                value={priority}
                onChange={setPriority}
                label={t("support.priority")}
                options={PRIORITIES.map((p) => ({
                  value: p,
                  label: t(`support.priorityLabel.${p}`),
                }))}
                className="w-full"
              />
            </div>
            <div className="min-w-0">
              <span className="crm-lab !mt-0">{t("support.department")}</span>
              <ComboField
                value={department}
                onChange={setDepartment}
                options={departments}
                label={t("support.department")}
                placeholder={t("support.departmentPlaceholder")}
              />
            </div>
          </div>

          <div>
            <span className="crm-lab !mt-0">{t("support.assignee")}</span>
            <MenuField
              variant="form"
              value={assignedTo}
              onChange={setAssignedTo}
              label={t("support.assignee")}
              // Somebody who has left the company is still on this list, last
              // and named as such. Dropping them would make a ticket that is
              // already theirs unreadable here; leaving them unmarked hands new
              // work to a desk nobody sits at. A `MenuField` option is a string
              // and nothing more, so the mark has to live in the label.
              options={[
                { value: UNASSIGNED, label: t("support.unassigned") },
                ...staff.map((s) => ({ value: s.id, label: staffOptionLabel(s, t("common.inactive")) })),
              ]}
              className="w-full"
            />
          </div>

          <div>
            <span className="crm-lab !mt-0">{t("support.firstMessage")}</span>
            <textarea
              className="crm-area"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder={t("support.firstMessagePlaceholder")}
            />
          </div>

          <div className="crm-m-foot">
            <Btn type="button" onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </Btn>
            <Btn type="submit" tone="brand" disabled={busy || !subject.trim()}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
              {t("common.add")}
            </Btn>
          </div>
        </form>
      </PopHost>
    </Modal>
  );
}
